<script setup lang="ts">
import { computed, type VNode } from 'vue'
import UiNavLink from '../UiNavLink.vue'
import UiIcon, { type UiIconProps } from '../UiIcon.vue'
import UiText from '../UiText.vue'
import { useButtonBase } from './composables/useButtonBase'
import { useButtonToggle } from './composables/useButtonToggle'
import { 
    getVariantClasses, 
    getTransitionClasses, 
    getBackgroundHoverClasses,
    getEffectiveHoverStyle,
    getPressedClasses,
    getActivePressedClasses,
} from './utils/buttonClasses'
import type { ButtonBaseProps, ButtonToggleProps } from './types'

export interface UiSquareButtonProps extends ButtonBaseProps, ButtonToggleProps {
    /** Text label (optional for square buttons) */
    label?: string
    /** Icon name to show */
    icon?: UiIconProps['name']
}

const props = withDefaults(defineProps<UiSquareButtonProps>(), {
    variant: 'ghost',
    theme: 'light',
    disabled: false,
    noHover: false,
})

const model = defineModel<boolean | undefined>({ default: undefined })

const { isLink, isExternal, effectiveNoHover } = useButtonBase(props)

const { isToggled, isDisabled, handleClick, handleMouseEnter } = useButtonToggle({
    toggle: props.toggle,
    value: props.value,
    disabled: props.disabled,
    modelValue: model,
})

const effectiveVariant = computed(() => {
    if (isToggled.value && props.activeVariant) {
        return props.activeVariant
    }
    return props.variant
})

const effectiveHoverStyle = computed(() => getEffectiveHoverStyle(props.hoverStyle, effectiveVariant.value))

const dynamicClasses = computed(() => {
    const noHover = effectiveNoHover.value || props.noHover || isToggled.value

    const styleClasses = isToggled.value
        ? getPressedClasses({ variant: effectiveVariant.value, theme: props.theme })
        : getVariantClasses({
            variant: effectiveVariant.value,
            theme: props.theme,
            disabled: isDisabled.value,
        })

    const transitionClasses = getTransitionClasses({
        variant: effectiveVariant.value,
        hoverStyle: effectiveHoverStyle.value,
        theme: props.theme,
        noHover,
    })

    const backgroundHoverClasses = getBackgroundHoverClasses({
        variant: effectiveVariant.value,
        hoverStyle: effectiveHoverStyle.value,
        theme: props.theme,
        noHover,
    })

    const activePressedClasses = !props.toggle && !props.value
        ? getActivePressedClasses({ variant: effectiveVariant.value, theme: props.theme })
        : []

    return [
        {
            ...styleClasses,
            'cursor-pointer': !isDisabled.value,
            [props.class || '']: Boolean(props.class),
        },
        transitionClasses,
        backgroundHoverClasses,
        activePressedClasses,
    ]
})

defineSlots<{
    before_label: () => VNode
    after_label: () => VNode
}>()
</script>

<template>
    <component 
        :is="isLink ? UiNavLink : 'button'"
        :to="isLink && !isExternal ? props.href : undefined"
        :href="isLink ? props.href : undefined"
        :external="isExternal"
        :target="isLink ? props.target : undefined"
        :rel="isLink ? props.rel : undefined"
        :allow-referrer="isLink ? props.allowReferrer : undefined"
        :type="!isLink ? 'button' : undefined"
        :disabled="!isLink && isDisabled"
        :aria-pressed="(props.toggle || props.value !== undefined) ? isToggled : undefined"
        class="group/ui-button inline-flex items-center justify-center gap-1.5 p-2 min-w-11 min-h-11 rounded-sm text-se-dark"
        :class="dynamicClasses"
        @click="handleClick"
        @mouseenter="handleMouseEnter"
    >
        <slot name="before_label">
            <UiIcon v-if="props.icon" :name="props.icon" class="w-5 h-5" />
        </slot>
        <UiText v-if="props.label" as="button" no-wrap :text="label" />
        <slot name="after_label" />
    </component>
</template>
